import React from 'react';
import { CheckCircle2, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { servicesData } from '../mockData';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

export const Services = () => {
  return (
    <div className="min-h-screen pt-16">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            AI Transformation Services
          </h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            End-to-end AI solutions that transform strategy into measurable business results
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {servicesData.map((service, index) => (
            <div 
              key={service.id} 
              className={`mb-20 ${index !== servicesData.length - 1 ? 'pb-20 border-b border-gray-200' : ''}`}
            >
              <div className={`grid lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                {/* Content */}
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="inline-block px-4 py-2 bg-blue-50 rounded-full mb-4">
                    <span className="text-blue-900 font-semibold text-sm">
                      Service {String(index + 1).padStart(2, '0')}
                    </span>
                  </div>
                  <h2 className="text-4xl font-bold text-gray-900 mb-6">
                    {service.title}
                  </h2>
                  <p className="text-lg text-gray-700 leading-relaxed mb-8">
                    {service.fullDesc}
                  </p>

                  {/* Benefits */}
                  <div className="mb-8">
                    <h3 className="text-xl font-bold text-gray-900 mb-4">Key Benefits</h3>
                    <div className="space-y-3">
                      {service.benefits.map((benefit, idx) => (
                        <div key={idx} className="flex items-start gap-3">
                          <CheckCircle2 className="w-6 h-6 text-blue-900 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700">{benefit}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Use Cases Accordion */}
                  <div className="mb-8">
                    <h3 className="text-xl font-bold text-gray-900 mb-4">Use Cases</h3>
                    <Accordion type="single" collapsible className="w-full">
                      {service.useCases.map((useCase, idx) => (
                        <AccordionItem key={idx} value={`item-${idx}`}>
                          <AccordionTrigger className="text-left text-gray-700 hover:text-blue-900">
                            {useCase}
                          </AccordionTrigger>
                          <AccordionContent className="text-gray-600">
                            Real-world application demonstrating how this service solves specific business challenges and delivers measurable results.
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </div>

                  <Button 
                    size="lg"
                    className="bg-blue-900 hover:bg-blue-800 text-white"
                    onClick={() => window.location.href = '/contact'}
                  >
                    Get Started
                    <ArrowRight className="ml-2" size={18} />
                  </Button>
                </div>

                {/* Image */}
                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <div className="relative">
                    <div className="absolute -inset-4 bg-gradient-to-br from-blue-100 to-blue-50 rounded-2xl transform rotate-3" />
                    <img 
                      src={service.image}
                      alt={service.title}
                      className="relative rounded-2xl shadow-2xl w-full h-96 object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Our Process
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              A proven methodology for delivering successful projects
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                step: '01',
                title: 'Discovery',
                description: 'We start by understanding your business, challenges, and goals through in-depth consultation.'
              },
              {
                step: '02',
                title: 'Strategy',
                description: 'Our team develops a tailored strategy and roadmap aligned with your objectives.'
              },
              {
                step: '03',
                title: 'Execution',
                description: 'We implement the solution using agile methodology with regular feedback cycles.'
              },
              {
                step: '04',
                title: 'Support',
                description: 'Post-launch, we provide ongoing support, monitoring, and optimization.'
              }
            ].map((process, index) => (
              <Card key={index} className="border-0 shadow-lg relative overflow-hidden group">
                <div className="absolute top-0 right-0 w-32 h-32 bg-blue-900 opacity-5 rounded-bl-full transform translate-x-8 -translate-y-8 group-hover:scale-150 transition-transform duration-500" />
                <CardContent className="p-8 relative">
                  <div className="text-6xl font-bold text-blue-900 opacity-20 mb-4">
                    {process.step}
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    {process.title}
                  </h3>
                  <p className="text-gray-600">
                    {process.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              AI & ML Technologies
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Leading-edge AI platforms and frameworks we specialize in
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
            {[
              'GPT-4', 'Claude', 'LangChain', 'TensorFlow', 'PyTorch', 'Gemini',
              'Azure OpenAI', 'AWS Bedrock', 'Hugging Face', 'LlamaIndex', 'Vector DBs', 'FastAPI'
            ].map((tech, index) => (
              <div 
                key={index}
                className="bg-white border-2 border-gray-200 rounded-xl p-6 text-center hover:border-blue-900 hover:shadow-lg transition-all duration-300 group"
              >
                <p className="font-semibold text-gray-700 group-hover:text-blue-900 transition-colors">
                  {tech}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-blue-100 mb-10">
            Let's discuss which services can best help your business grow
          </p>
          <Button 
            size="lg"
            className="bg-white text-blue-900 hover:bg-gray-100 text-lg px-10 py-6 h-auto font-semibold"
            onClick={() => window.location.href = '/contact'}
          >
            Schedule a Consultation
            <ArrowRight className="ml-2" size={20} />
          </Button>
        </div>
      </section>
    </div>
  );
};
